import 'styles/font.css';

import React, { useEffect, useLayoutEffect, useState } from 'react';

import { NextPage } from 'next';
import Head from 'next/head';
import Script from 'next/script';
import { useRouter } from 'next/router';
import { Outfit } from 'next/font/google';
import type { AppContext, AppProps } from 'next/app';
import absoluteUrl from 'next-absolute-url';

import { LicenseInfo } from '@mui/x-license-pro';
import CssBaseline from '@mui/material/CssBaseline';

import { DarkThemeProvider } from '@lemon-io/ui-kit';
import mixpanel from 'mixpanel-browser';

import useGtm from 'Hooks/use-gtm';
import { getAuthCookies } from 'Utils/getCookies';
import { MEvents, PageViewedMEvent } from 'Utils/mixpanel';
import { muiLicense } from 'Config/app';
import Routes from 'Config/routes';
import { getCookie } from 'cookies-next';
import {
  QueryClient,
  QueryClientProvider,
  HydrationBoundary,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import dynamic from 'next/dynamic';
import MixpanelService from 'Services/mixpanel-service';
import { AppCacheProvider } from '@mui/material-nextjs/v14-pagesRouter';

const Layout = dynamic(() => import('Components/common/layout'));

LicenseInfo.setLicenseKey(muiLicense);

export type NextPageWithLayout<P = unknown, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
  pageProps: AppProps['pageProps'] & {
    withoutLayout?: boolean;
    dehydratedState?: Record<string, unknown>;
    onlyHeader?: boolean;
    token: string;
  };
};

export const outfit = Outfit({
  display: 'swap',
  weight: ['400', '600', '700'],
  subsets: ['latin'],
});

export interface MyAppProps extends AppPropsWithLayout {
  token: string;
}
function MyApp(props: MyAppProps) {
  const [eventSent, setEventSent] = useState(false);
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
            refetchOnWindowFocus: false,
            retry: 2,
          },
        },
      })
  );

  const { Component, pageProps } = props;

  useGtm('GTM-TJTHKK6');

  const router = useRouter();

  useLayoutEffect(() => {
    MixpanelService.init();
    MixpanelService.addEvent(MEvents.SessionStart);

    return () => {
      MixpanelService.addEvent(MEvents.SessionEnd);
    };
  }, []);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (!url.includes('escape-the-matrix')) {
        PageViewedMEvent(url);
        return;
      }

      if (eventSent || getCookie('authDomainSSO')) {
        return;
      }

      setEventSent(true);
      mixpanel.track('me-lemon_registration_form_viewed');
    };
    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.events, eventSent]);

  const getLayout: (page: React.ReactElement) => React.ReactNode =
    pageProps?.withoutLayout
      ? page => page
      : page => (
          <Layout token={pageProps?.token} onlyHeader={!!pageProps?.onlyHeader}>
            {page}
          </Layout>
        );

  return (
    <AppCacheProvider {...props}>
      <Head>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
      </Head>
      <Script
        id="hotjar-script"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `(function(h,o,t,j,a,r){
          h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
          h._hjSettings={hjid:1723409,hjsv:6};
          a=o.getElementsByTagName('head')[0];
          r=o.createElement('script');r.async=1;
          r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
          a.appendChild(r);
      })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
        }}
      />
      <style jsx global>{`
        html,
        body {
          font-family: ${outfit.style.fontFamily};
        }

        #__next {
          height: 100vh;
        }
      `}</style>
      <QueryClientProvider client={queryClient}>
        <DarkThemeProvider>
          <CssBaseline />
          <HydrationBoundary state={pageProps?.dehydratedState}>
            {getLayout(<Component {...pageProps} />)}
          </HydrationBoundary>
        </DarkThemeProvider>
        <ReactQueryDevtools initialIsOpen={false} position="right" />
      </QueryClientProvider>
    </AppCacheProvider>
  );
}

MyApp.getInitialProps = ({ ctx }: AppContext) => {
  const auth = getAuthCookies(ctx);

  const { res, req } = ctx;
  const isServer = res && typeof res?.writeHead === 'function';
  const { protocol, host } = absoluteUrl(req);

  // skip this if dev mode to be able to set cookies manualy
  if (process.env.NODE_ENV === 'development' && !auth) {
    return {};
  }

  // if env is dev, and cookies from prod
  if (
    process.env.NODE_ENV === 'development' &&
    auth?.environment === 'production'
  ) {
    return {};
  }

  if (
    !auth &&
    isServer &&
    !(
      ctx.asPath?.includes(Routes.ViewProject) ||
      ctx.asPath?.includes(Routes.EscapeTheMatrix)
    )
  ) {
    const redirect = `${protocol}//${host}${ctx.asPath ?? ''}`;
    const loginUrl = new URL(
      `${process.env.APP_LEMON_URL as string}/auth/login`
    );
    loginUrl.searchParams.append('redirect', redirect);

    res.writeHead(307, {
      Location: loginUrl.toString(),
    });
    res.end();

    return {};
  }

  if (auth) {
    return {
      token: auth.token,
    };
  }

  return {};
};

export default MyApp;
